import { useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { BarChart3, Boxes, Briefcase, ClipboardList, CreditCard, LifeBuoy, LineChart, Network, Package, ScrollText, Settings, ShoppingCart, SlidersHorizontal, Truck, Users, Wallet } from "lucide-react";
import { useEffect } from "react";
import { useAdminApi } from "@/lib/admin";
import { Logo } from "@/components/site/Header";
import Overview from "@/components/admin/Overview";
import Applications from "@/components/admin/Applications";
import Jobs from "@/components/admin/Jobs";
import Payments from "@/components/admin/Payments";
import Products from "@/components/admin/Products";
import Orders from "@/components/admin/Orders";
import { UsersPage, AuditPage, SettingsPage } from "@/components/admin/Misc";
import Distributors from "@/components/admin/mlm/Distributors";
import MlmPackages from "@/components/admin/mlm/Packages";
import PackageOrders from "@/components/admin/mlm/PackageOrders";
import Commissions from "@/components/admin/mlm/Commissions";
import { Reports, Support, MlmSettings } from "@/components/admin/mlm/Misc";

const TABS = [
  ["", "Overview", BarChart3, "staff"], ["distributors", "Distributors", Network, "staff"], ["packages", "Packages", Boxes, "staff"],
  ["package-orders", "Package Orders", Truck, "staff"], ["commissions", "Commissions & Payouts", Wallet, "staff"], ["mlm-reports", "Sales & Team Reports", LineChart, "staff"],
  ["support", "Support & Notifications", LifeBuoy, "staff"], ["mlm-settings", "MLM Settings", SlidersHorizontal, "admin"], ["applications", "Applications", ClipboardList, "staff"], ["vacancies", "Vacancies", Briefcase, "staff"],
  ["payments", "Payments & Refunds", CreditCard, "staff"], ["products", "Products", Package, "staff"], ["orders", "Product Orders", ShoppingCart, "staff"],
  ["users", "Admin Users", Users, "admin"], ["audit", "Audit Logs", ScrollText, "admin"], ["settings", "Site Settings", Settings, "admin"],
];

const Shell = () => {
  const call = useAdminApi();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    call("get", "/me").then(setMe).catch(() => setErr(true));
  }, [call]);
  if (err) return <p className="p-10">Could not verify your access. Refresh to retry.</p>;
  if (!me) return <p className="p-10 text-slate-500">Checking access…</p>;
  if (me.role === "member")
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center" data-testid="admin-access-pending">
        <div className="max-w-md space-y-4"><h1 className="font-display text-2xl font-bold">Access not granted</h1><p className="text-slate-600">Your account ({me.email}) is not an authorised administrator. Ask an existing BMI SP GROUP admin to grant you access.</p><div className="flex justify-center"><UserButton /></div></div>
      </div>
    );
  const isAdmin = me.role === "admin";
  const tabs = TABS.filter((t) => t[3] === "staff" || isAdmin);
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:flex-row">
      <aside className="bg-slate-950 text-slate-300 lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0">
        <div className="flex items-center justify-between p-5"><Logo light /><div className="lg:hidden"><UserButton /></div></div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible">
          {tabs.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin/${to}`} end className={({ isActive }) => `flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-amber-600 text-white" : "hover:bg-slate-800 hover:text-white"}`} data-testid={`admin-tab-${to || "overview"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 border-t border-slate-800 p-5 text-xs lg:flex"><UserButton /><span className="truncate" data-testid="admin-role">{me.email} · {me.role}</span></div>
      </aside>
      <main className="min-w-0 flex-1 p-4 sm:p-8">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="distributors" element={<Distributors isAdmin={isAdmin} />} />
          <Route path="packages" element={<MlmPackages isAdmin={isAdmin} />} />
          <Route path="package-orders" element={<PackageOrders />} />
          <Route path="commissions" element={<Commissions isAdmin={isAdmin} />} />
          <Route path="mlm-reports" element={<Reports />} />
          <Route path="support" element={<Support isAdmin={isAdmin} />} />
          {isAdmin && <Route path="mlm-settings" element={<MlmSettings />} />}
          <Route path="applications" element={<Applications isAdmin={isAdmin} />} />
          <Route path="vacancies" element={<Jobs isAdmin={isAdmin} />} />
          <Route path="payments" element={<Payments isAdmin={isAdmin} />} />
          <Route path="products" element={<Products isAdmin={isAdmin} />} />
          <Route path="orders" element={<Orders isAdmin={isAdmin} />} />
          {isAdmin && <Route path="users" element={<UsersPage />} />}
          {isAdmin && <Route path="audit" element={<AuditPage />} />}
          {isAdmin && <Route path="settings" element={<SettingsPage />} />}
        </Routes>
      </main>
    </div>
  );
};

export default function Admin() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <p className="p-10 text-slate-500">Loading…</p>;
  return (
    <>
      <Show when="signed-in"><Shell /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
